import { computed, inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular/standalone';
import { TtSimpleModalComponent } from '@techniek-team/modal';
import { firstEmitFrom } from '@techniek-team/rxjs';
import { LoginResponse, LogoutAuthOptions, OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { TT_AUTH_CONFIGS } from '../../auth.provider';
import { LoggerWithConfigurationService } from '../internal/logger-with-configuration.service';
import { StoragePersistenceService } from '../internal/storage-persistence.service';
import { LogoutService } from '../logout/logout.service';

@Injectable({
  providedIn: 'root',
})
export class OAuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  private readonly loggerService = inject(LoggerWithConfigurationService);

  private readonly storage = inject(StoragePersistenceService);

  private readonly logoutService = inject(LogoutService);

  private readonly toastController = inject(ToastController);

  private readonly modalController = inject(ModalController);

  private config = inject(TT_AUTH_CONFIGS);

  private router = inject(Router);

  private activatedRoute = inject(ActivatedRoute);

  public authorize() {
    this.oidcSecurityService.authorize();
  }

  public async authorizeWithPopUp(withRedirect = false): Promise<LoginResponse | undefined> {
    const result = await firstValueFrom(this.oidcSecurityService.authorizeWithPopUp());
    if (withRedirect && result.isAuthenticated) {
      this.redirectOnPopupLogin();
    }

    return result;
  }

  public readonly authenticated = computed(
    () => this.oidcSecurityService.authenticated().isAuthenticated,
  );

  public isAuthenticated(configId?: string): Promise<boolean> {
    return firstValueFrom(this.oidcSecurityService.isAuthenticated(configId), {
      defaultValue: false,
    });
  }

  public async requestIfTheUserWantsToLogOut(configId?: string): Promise<void> {
    const modal: HTMLIonModalElement = await this.modalController.create({
      component: TtSimpleModalComponent,
      componentProps: {
        title: 'Uitloggen?',
        message: 'Weet je zeker dat je wilt uitloggen?',
        buttons: [
          {
            text: 'Nee, bedankt',
            role: 'cancel',
            fill: 'clear',
          },
          {
            text: 'Ja, graag',
            fill: 'clear',
            handler: () => this.logout(configId),
          },
        ],
      },
      cssClass: ['no-fullscreen-in-compact-mode'],
    });
    await modal.present();
    await modal.onWillDismiss();
  }

  public logout(configId?: string): Promise<boolean> {
    return firstEmitFrom(
      this.logoff(configId, {
        logoffMethod: 'POST',
      }),
    )
      .then((result) => {
        return true;
      })
      .catch(() => false)
      .finally(() => {
        this.oidcSecurityService.logoffLocal(configId);
        void this.router.navigate(['login']);
      });
  }

  public logoff(configId?: string, logoutAuthOptions?: LogoutAuthOptions): Observable<unknown> {
    return this.oidcSecurityService
      .logoff(configId, logoutAuthOptions)
      .pipe(tap((result) => this.loggerService.logDebug('Logout attempt results', result)));
  }

  public logoffAndRevokeTokens(
    configId?: string,
    logoutAuthOptions?: LogoutAuthOptions,
  ): Observable<unknown> {
    return this.oidcSecurityService.logoffAndRevokeTokens(configId, logoutAuthOptions);
  }

  public getForbiddenUrlTree(configId?: string): UrlTree | boolean {
    const config = this.getConfig(configId);

    if (!config.forbiddenRoute) {
      return this.getUnauthorizedUrlTree(configId);
    }

    return this.router.parseUrl(config.forbiddenRoute);
  }

  public getUnauthorizedUrlTree(configId?: string): UrlTree | boolean {
    const config = this.getConfig(configId);

    if (!config.unauthorizedRoute) {
      return false;
    }

    return this.router.parseUrl(config.unauthorizedRoute);
  }

  private getConfig(configId?: string) {
    let config = this.config[0];
    if (configId) {
      config = this.config.find((item) => item.configId === configId) ?? config;
    }
    return config;
  }

  public redirectOnPopupLogin(): void {
    const config = this.getConfig();
    const savedRouteForRedirect = this.storage.read('redirect', config);

    if (savedRouteForRedirect !== null) {
      this.storage.remove('redirect', config);
      void this.router.navigateByUrl(savedRouteForRedirect);
    }
    const routeSegments = this.activatedRoute.snapshot.url.map((segment) => segment.toString());
    const isLoginRoute = [
      config.unauthorizedRoute,
      config.forbiddenRoute,
      config.postLogoutRedirectUri,
    ].some((route) => {
      return typeof route === 'string' && routeSegments.includes(route);
    });
    if (isLoginRoute || routeSegments.length === 0) {
      void this.router.navigate(['/'], { onSameUrlNavigation: 'reload' });
    }
  }
}
